// Deterministic mock availability. A doctor's diary is mostly full for `fullyBookedDays`,
// patchy for the week after, then mostly open — mirroring clinics where patients book 2–3 weeks ahead.
import { db, clinic } from './db';
import { DAY_PERIODS, FEW_SLOTS_THRESHOLD } from '../../utils/constants';
import {
  addDays, fromDateKey, toDateKey, today, daysBetween, timeToMinutes, minutesToTime,
} from '../../utils/date';

const hash = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
};

export const allSlotTimes = () => {
  const times = [];
  const start = timeToMinutes(DAY_PERIODS[0].from);
  const end = timeToMinutes(DAY_PERIODS[DAY_PERIODS.length - 1].to);
  for (let m = start; m < end; m += clinic.slotMinutes) times.push(minutesToTime(m));
  return times;
};

const isWorkingDay = (doctor, date) =>
  clinic.workingDays.includes(date.getDay()) && doctor.workingDays.includes(date.getDay());

const bookedRatio = (doctor, lead) => {
  if (lead < doctor.fullyBookedDays) return 0.997;
  if (lead < doctor.fullyBookedDays + 4) return 0.55;
  return 0.25;
};

/** Returns [{ time, available }] for a doctor on a date key. */
export const slotsFor = (doctor, dateKey, { excludeAppointmentId } = {}) => {
  const date = fromDateKey(dateKey);
  const lead = daysBetween(today(), date);
  if (lead < 0 || lead > clinic.bookingWindowDays || !isWorkingDay(doctor, date)) return [];

  const taken = new Set(
    db.state.appointments
      .filter(
        (a) => a.doctorId === doctor.id && a.date === dateKey && a.status !== 'cancelled' && a.id !== excludeAppointmentId,
      )
      .map((a) => a.time),
  );

  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const ratio = bookedRatio(doctor, lead);

  return allSlotTimes().map((time) => {
    const past = lead === 0 && timeToMinutes(time) <= nowMins + 60;
    const randomlyBooked = hash(`${doctor.id}|${dateKey}|${time}`) < ratio;
    return { time, available: !past && !randomlyBooked && !taken.has(time) };
  });
};

export const daySummary = (doctor, dateKey, opts) => {
  const slots = slotsFor(doctor, dateKey, opts);
  const open = slots.filter((s) => s.available).length;
  let status = 'closed';
  if (slots.length) status = open === 0 ? 'full' : open <= FEW_SLOTS_THRESHOLD ? 'few' : 'available';
  return { date: dateKey, status, slotsOpen: open };
};

export const nextAvailable = (doctor, limit = 1) => {
  const found = [];
  const start = today();
  for (let i = 0; i <= clinic.bookingWindowDays && found.length < limit; i += 1) {
    const key = toDateKey(addDays(start, i));
    const slots = slotsFor(doctor, key);
    const first = slots.find((s) => s.available);
    if (first) {
      const summary = daySummary(doctor, key);
      found.push({ date: key, time: first.time, slotsOpen: summary.slotsOpen, status: summary.status });
    }
  }
  return found;
};
